import React, { useEffect, useState } from "react";
import { CalendarX, ArrowRight } from "lucide-react";
import { joinDropList, getDropListCount } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const Events = () => {
  const { user } = useAuth();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [joined, setJoined] = useState(false);
  const [busy, setBusy] = useState(false);
  const [count, setCount] = useState(0);

  useEffect(() => {
    getDropListCount().then((r) => setCount(r.count || 0)).catch(() => {});
  }, []);

  useEffect(() => {
    if (user?.email) setEmail(user.email);
    if (user?.name) setName(user.name);
  }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      const res = await joinDropList({ email, name });
      toast.success(res.message);
      if (typeof res.count === "number") setCount(res.count);
      setJoined(true);
    } catch {
      toast.error("Something broke. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="events-page" className="px-5 md:px-10 py-14 min-h-screen">
      <div className="border-b border-border pb-8 mb-16">
        <h1 className="font-serif text-6xl sm:text-7xl uppercase tracking-tighter leading-none">
          Events
        </h1>
        <p className="mt-4 font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">
          Runway shows, drops & appearances
        </p>
      </div>

      {/* DROP LIST */}
      <section
        data-testid="droplist-section"
        className="relative overflow-hidden border border-border p-8 md:p-14 mb-16"
      >
        <div
          className="pointer-events-none absolute inset-0 opacity-40"
          style={{
            background:
              "radial-gradient(circle at 80% 20%, rgba(91,42,134,0.35) 0%, transparent 55%)",
          }}
        />
        <div className="relative max-w-2xl">
          <p className="font-mono text-[10px] uppercase tracking-[0.4em] text-[#5B2A86] mb-4">
            The Drop List
          </p>
          <h2 className="font-serif text-4xl sm:text-5xl uppercase tracking-tighter leading-none">
            Walk first through the gate
          </h2>
          <p className="mt-5 font-mono text-xs uppercase tracking-[0.18em] text-muted-foreground leading-relaxed max-w-xl">
            Every piece is made in limited runs. Join the drop list and you'll be the first to
            know the moment a new collection descends — before it reaches anyone else.
          </p>

          {joined ? (
            <div
              data-testid="droplist-success"
              className="mt-10 border border-[#5B2A86] bg-[#5B2A86]/10 px-6 py-5"
            >
              <p className="font-serif text-2xl uppercase tracking-tight">You're on the list.</p>
              <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                Stay close. The next walk is coming.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="droplist-form" className="mt-10 space-y-4 max-w-md">
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="NAME (OPTIONAL)"
                data-testid="droplist-name"
                className="w-full bg-transparent border border-border px-4 py-3 font-mono text-xs uppercase tracking-[0.15em] placeholder:text-muted-foreground focus:outline-none focus:border-foreground"
              />
              <div className="flex">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="YOUR@EMAIL.COM"
                  data-testid="droplist-email"
                  className="flex-1 bg-transparent border border-border px-4 py-3 font-mono text-xs uppercase tracking-[0.15em] placeholder:text-muted-foreground focus:outline-none focus:border-foreground"
                />
                <button
                  type="submit"
                  disabled={busy}
                  data-testid="droplist-submit"
                  className="bg-foreground text-background px-6 flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] hover:bg-[#5B2A86] hover:text-foreground transition-colors disabled:opacity-50"
                >
                  {busy ? "…" : "Join"}
                  {!busy && <ArrowRight className="w-4 h-4" />}
                </button>
              </div>
            </form>
          )}

          {count > 0 && (
            <p
              data-testid="droplist-count"
              className="mt-6 font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground"
            >
              {count} {count === 1 ? "soul walks" : "souls walk"} with the list
            </p>
          )}
        </div>
      </section>

      {/* UPCOMING SHOWS */}
      <div
        data-testid="events-empty"
        className="flex flex-col items-center justify-center text-center py-24 border border-border"
      >
        <CalendarX className="w-10 h-10 text-[#5B2A86] mb-6" strokeWidth={1.5} />
        <h2 className="font-serif text-3xl sm:text-4xl uppercase tracking-tight">
          No Upcoming Events
        </h2>
        <p className="mt-4 font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground max-w-md">
          Nothing on the calendar right now. New shows and ticket links will appear here — join the
          drop list above to hear it first.
        </p>
      </div>
    </div>
  );
};

export default Events;
